import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Search,
} from "lucide-react";
import {
  useV2AtRiskAccounts,
  useV2Accounts,
  useV2RecentActivity,
  useV2Liquidations,
} from "~/hooks/useV2Data";
import { useState, useEffect } from "react";

interface V2AccountActivityProps {
  // No props needed - data fetched internally
}

export function V2AccountActivity({}: V2AccountActivityProps) {
  const {
    data: atRiskAccounts,
    isLoading: atRiskLoading,
    error: atRiskError,
  } = useV2AtRiskAccounts();
  const { data: allAccounts, isLoading: accountsLoading } = useV2Accounts();
  const { data: recentActivity, isLoading: activityLoading } =
    useV2RecentActivity();
  const { data: liquidations, isLoading: liquidationsLoading } =
    useV2Liquidations();

  // Sorting state
  const [sortField, setSortField] = useState<
    "healthFactor" | "collateral" | "debt"
  >("healthFactor");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Reset to first page when sorting or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortField, sortDirection, searchQuery]);

  const isLoading =
    atRiskLoading || accountsLoading || activityLoading || liquidationsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (
    atRiskError ||
    !atRiskAccounts ||
    !allAccounts ||
    !recentActivity ||
    !liquidations
  ) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Failed to load account data
          </p>
        </div>
      </div>
    );
  }
  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatGOHM = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getHealthFactorColor = (healthFactor: number) => {
    if (healthFactor < 1.2) return "text-red-600";
    if (healthFactor < 1.5) return "text-orange-600";
    return "text-green-600";
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "borrow":
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      case "repay":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case "collateralAdd":
      case "collateral_add":
        return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case "collateralWithdraw":
      case "collateral_withdraw":
        return <TrendingDown className="h-4 w-4 text-orange-500" />;
      case "liquidation":
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getActivityLabel = (type: string) => {
    switch (type) {
      case "borrow":
        return "Borrow";
      case "repay":
        return "Repay";
      case "collateralAdd":
      case "collateral_add":
        return "Add Collateral";
      case "collateralWithdraw":
      case "collateral_withdraw":
        return "Withdraw Collateral";
      case "liquidation":
        return "Liquidation";
      default:
        return type;
    }
  };

  // Handle sorting
  const handleSort = (field: "healthFactor" | "collateral" | "debt") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filter accounts based on search query
  const filteredAccounts = allAccounts.filter((account) => {
    if (!searchQuery) return true;
    return account.address.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Sort accounts based on current sort field and direction
  const sortedAccounts = [...filteredAccounts].sort((a, b) => {
    let aValue: number;
    let bValue: number;

    switch (sortField) {
      case "healthFactor":
        aValue = a.healthFactor;
        bValue = b.healthFactor;
        break;
      case "collateral":
        aValue = a.collateral;
        bValue = b.collateral;
        break;
      case "debt":
        aValue = a.debt;
        bValue = b.debt;
        break;
      default:
        aValue = a.healthFactor;
        bValue = b.healthFactor;
    }

    if (sortDirection === "asc") {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  const getSortIcon = (field: "healthFactor" | "collateral" | "debt") => {
    if (sortField !== field) {
      return <ChevronUp className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDown className="h-4 w-4 text-blue-600" />
    );
  };

  // Pagination calculations
  const totalPages = Math.ceil(sortedAccounts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAccounts = sortedAccounts.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Account Activity</h3>

        {/* Recent Activity */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentActivity.slice(0, 10).map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    {getActivityIcon(activity.type)}
                    <div>
                      <div className="font-medium">
                        {getActivityLabel(activity.type)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatAddress(activity.account)} •{" "}
                        {formatDate(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">
                      {activity.type.includes("collateral")
                        ? `${formatGOHM(activity.amount)} gOHM`
                        : formatUSD(activity.amount)}
                    </div>
                    <a
                      href={`https://etherscan.io/tx/${activity.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:text-blue-700"
                    >
                      View Tx
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Liquidations */}
        {liquidations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Recent Liquidations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {liquidations.slice(0, 5).map((liquidation) => (
                  <div
                    key={liquidation.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20"
                  >
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <div>
                        <div className="font-medium">Liquidation</div>
                        <div className="text-sm text-muted-foreground">
                          {formatAddress(liquidation.account)} •{" "}
                          {formatDate(liquidation.timestamp)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">
                        {formatUSD(liquidation.amount)}
                      </div>
                      <a
                        href={`https://etherscan.io/tx/${liquidation.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:text-blue-700"
                      >
                        View Tx
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Accounts Table */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>All Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by wallet address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort("collateral")}
                    >
                      <div className="flex items-center gap-1">
                        Collateral
                        {getSortIcon("collateral")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort("debt")}
                    >
                      <div className="flex items-center gap-1">
                        Debt
                        {getSortIcon("debt")}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentAccounts.map((account) => (
                    <TableRow key={account.address}>
                      <TableCell className="font-mono">
                        <a
                          href={`https://etherscan.io/address/${account.address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          {formatAddress(account.address)}
                        </a>
                      </TableCell>
                      <TableCell>
                        {formatGOHM(account.collateral)} gOHM
                      </TableCell>
                      <TableCell>{formatUSD(account.debt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, sortedAccounts.length)} of{" "}
                  {sortedAccounts.length} accounts
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
